import { observer } from 'mobx-react-lite';
import React, { ReactNode } from 'react';
import { makeStyles } from 'tss-react/mui';
import BookingSummaryToggle from '#/components/BookingSummaryPanel/BookingSummaryToggle';
import { useAuthStore } from '#/hooks/useAuthStore';
import { useRootStore } from '#/hooks/useRootStore';
import { MenuItem } from '#/lib/MenuItem';
import { AppBarProps } from '@mui/material';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import MuiLink from '@mui/material/Link';
import Toolbar from '@mui/material/Toolbar';
import Link from 'next/link';
import { useRouter } from 'next/router';
import TestEnvRibbon from '../TestEnvRibbon';
import UserMenu from '../UserMenu';
import { Logo } from './Logo';

const useStyles = makeStyles()((theme) => ({
    homeLink: {
        color: theme.palette.secondary.main,
        display: 'flex',
        textDecoration: 'none',
        alignItems: 'center',
        marginRight: theme.spacing(3),
    },
    menu: {
        flexGrow: 1,
        display: 'flex',
    },
    menuItem: {
        marginRight: theme.spacing(2),
        '&.active': {
            textDecoration: 'underline',
        },
    },
    progressIndicator: {
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
    },
}));

interface TopbarProps extends AppBarProps {
    leftActions?: ReactNode;
    rightActions?: ReactNode;
}

const UserActions = observer(function UserActionsComponent() {
    const authStore = useAuthStore();
    const rootStore = useRootStore();
    const isLoading = authStore.isLoading || authStore.profileRefreshing;
    const showUserMenu = Boolean(!isLoading && authStore.isAuthenticated && authStore.profile);

    if (isLoading) {
        return (
            <Box
                className="animate__animated animate__fadeIn"
                sx={{
                    p: 2,
                }}
            >
                <CircularProgress size={30} thickness={2.5} />
            </Box>
        );
    }
    if (showUserMenu) {
        return (
            <div className="animate__animated animate__fadeIn">
                <UserMenu />
            </div>
        );
    }
    return (
        <div className="animate__animated animate__fadeIn">
            <MuiLink component={Link} variant="subtitle1" href={rootStore.brandWebsite}>
                Back to {rootStore.brand.toUpperCase()} website
            </MuiLink>
        </div>
    );
});
UserActions.displayName = 'UserActions';

const menuItems: MenuItem[] = [];
const Topbar: React.FunctionComponent<React.PropsWithChildren<TopbarProps>> = observer(function TopbarComponent({
    className,
    children,
    leftActions,
    rightActions,
    ...rest
}) {
    const { classes, cx } = useStyles();
    const { pathname } = useRouter();

    return (
        <AppBar {...rest} className={className} color="default" position="fixed">
            <TestEnvRibbon />
            <Toolbar>
                {leftActions}
                <Logo />
                <nav className={classes.menu}>
                    {menuItems.map((m) => (
                        <MuiLink
                            key={m.href}
                            component={Link}
                            href={m.href as string}
                            className={cx(classes.menuItem, { active: pathname === m.href })}
                            variant="subtitle1"
                        >
                            {m.primary}
                        </MuiLink>
                    ))}
                </nav>
                <UserActions />
                {children}
                <BookingSummaryToggle />
                {rightActions}
            </Toolbar>
        </AppBar>
    );
});

Topbar.displayName = 'Topbar';

export default React.memo(Topbar);
